import { ReactNode, useRef } from "react";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { CardContent } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import type { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import {
  getColWidth,
  SCHEDULE_CONSTANTS,
  getHolidayName,
  isWeekendDay
} from "../utils";
import { useHolidays } from "@/hooks/use-holidays";
import { cn } from "@/lib/utils";

dayjs.extend(isToday);

export interface ScheduleRowData {
  key: string | number;
  /** Content for the fixed left panel (sidebar job card). */
  left: ReactNode;
  /** Content for the horizontally-scrollable right grid cell (Gantt bars). */
  right: ReactNode;
  /** Row height in px — syncs the left and right cell heights. */
  height: number;
}

interface ScheduleCalendarContentProps {
  viewMode: ViewMode;
  timelineData: {
    days: Date[];
    startDate: Date;
    viewMode: ViewMode;
  };
  timeSlots: Array<{ hour: number; label: string; time: string }>;
  isEmpty: boolean;
  emptyMessage?: string;
  rowLabelHeader: string;
  /** Optional sub-text shown below the row label header in the left panel */
  rowLabelDescription?: string;
  /** Optional action slot rendered top-right of the left panel header (e.g. Unselect button) */
  headerAction?: ReactNode;
  /** Two-column row data. Left panel is fixed; right grid gets its own horizontal scrollbar. */
  rowData?: ScheduleRowData[];
  /** @deprecated Prefer rowData. Children rendered inside overflow-x-auto (legacy). */
  children?: ReactNode;
  zoomLevel?: number;
  /** Width of the left row-label panel in px. Defaults to 192 to preserve existing layouts. */
  leftPanelWidth?: number;
  timeLineClassName?: string;
}

export default function ScheduleCalendarContent({
  viewMode,
  timelineData,
  timeSlots,
  isEmpty,
  emptyMessage = "No data available",
  rowLabelHeader,
  rowLabelDescription,
  headerAction,
  rowData,
  children,
  zoomLevel = 100,
  leftPanelWidth = 192,
  timeLineClassName = ""
}: ScheduleCalendarContentProps) {
  const holidays = useHolidays();
  const colWidth = getColWidth(viewMode, zoomLevel);
  const totalGridCols =
    viewMode === "daily"
      ? SCHEDULE_CONSTANTS.TOTAL_COLUMNS
      : timelineData.days.length;
  const gridMinWidth = colWidth * totalGridCols;

  // Daily-view header: mirror the weekly/monthly weekend/holiday/today treatment so the single
  // day strip is tinted and shows the public-holiday name (WOR-2187). Precedence today > holiday
  // > weekend. The body strip is already tinted by DailyGridLines.
  const dailyDay = dayjs(timelineData.startDate);
  const dailyIsToday = dailyDay.isToday();
  const dailyHolidayName = getHolidayName(dailyDay, holidays);
  const dailyIsWeekend = isWeekendDay(dailyDay);
  const dailyHeaderBg = dailyIsToday
    ? "aero:bg-primary/10"
    : dailyHolidayName
      ? "aero:bg-yellow-200"
      : dailyIsWeekend
        ? "aero:bg-gray-200"
        : "aero:bg-muted/20";

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  const syncHeaderScroll = () => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  };

  /* ── Two-column layout (rowData prop) ───────────────────────────────── */
  if (rowData) {
    return (
      <CardContent className="aero:p-0">
        {/* Header: left panel + right header synced via ref */}
        <div className="aero:flex aero:border-b aero:border-border/50">
          <div
            className="aero:flex-shrink-0 aero:border-r aero:border-neutral-300 aero:bg-muted/20"
            style={{ width: `${leftPanelWidth}px` }}
          >
            {viewMode === "daily" ? (
              <div className="aero:flex aero:flex-col aero:justify-center aero:pt-8 aero:pb-4 aero:pl-5 aero:pr-4 aero:h-full">
                <div className="aero:flex aero:items-center aero:justify-between aero:mb-1">
                  <span className="aero:font-semibold aero:text-sm">
                    {rowLabelHeader}
                  </span>
                  {headerAction}
                </div>
                {rowLabelDescription && (
                  <p className="aero:text-[10px] aero:font-normal aero:text-muted-foreground aero:leading-tight">
                    {rowLabelDescription}
                  </p>
                )}
              </div>
            ) : (
              <div className="aero:py-3 aero:pl-4 aero:pr-3 aero:font-semibold aero:text-sm">
                <div className="aero:flex aero:items-center aero:justify-between">
                  <span>{rowLabelHeader}</span>
                  {headerAction}
                </div>
                {rowLabelDescription && (
                  <p className="aero:text-[10px] aero:font-normal aero:text-muted-foreground aero:mt-0.5 aero:leading-tight">
                    {rowLabelDescription}
                  </p>
                )}
              </div>
            )}
          </div>
          {/* Right header: overflow-x-hidden, scrollLeft synced to body */}
          <div
            ref={headerScrollRef}
            className="aero:flex-1 aero:overflow-x-hidden"
          >
            <div
              style={{
                minWidth: `${gridMinWidth + 20}px`,
                paddingLeft: "20px",
                marginTop: "20px"
              }}
            >
              {viewMode === "daily" ? (
                <div className="aero:flex aero:flex-col">
                  <div
                    className={cn(
                      "aero:border-b aero:border-t aero:border-neutral-300 aero:py-3 aero:px-5",
                      dailyHeaderBg
                    )}
                  >
                    <p
                      className={cn(
                        "aero:text-xs aero:font-semibold aero:uppercase aero:tracking-widest aero:leading-none aero:mb-1",
                        dailyIsToday
                          ? "aero:text-primary"
                          : "aero:text-muted-foreground"
                      )}
                    >
                      {dailyDay.format("ddd")}
                    </p>
                    <p
                      className={`aero:text-2xl aero:font-bold aero:leading-none ${
                        dailyIsToday
                          ? "aero:text-primary"
                          : "aero:text-foreground"
                      }`}
                    >
                      {dailyDay.format("D")}
                    </p>
                    {dailyHolidayName && (
                      <p
                        className="aero:mt-1 aero:text-[10px] aero:font-medium aero:leading-tight aero:text-amber-700 aero:truncate"
                        title={dailyHolidayName}
                      >
                        {dailyHolidayName}
                      </p>
                    )}
                  </div>
                  <div className="aero:flex aero:bg-muted/30">
                    {timeSlots.map((slot) => (
                      <div
                        key={slot.hour}
                        className={cn(
                          "aero:flex-shrink-0 aero:p-2 aero:text-center aero:text-xs aero:font-medium aero:border-r aero:last:border-r-0 aero:text-muted-foreground"
                        )}
                        style={{ width: colWidth }}
                      >
                        {slot.label}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={cn("aero:flex", timeLineClassName)}>
                  {timelineData.days.map((day) => {
                    return (
                      <div
                        key={day.toISOString()}
                        className="aero:flex-shrink-0 aero:pl-2 aero:pt-2 aero:pb-2 aero:border-r aero:border-t aero:border-neutral-300 aero:last:border-r-0 aero:h-[52px]"
                        style={{ width: colWidth }}
                      >
                        <p
                          className={`aero:text-[10px] aero:font-semibold aero:uppercase aero:tracking-widest aero:leading-none aero:mb-1 ${
                            dayjs(day).isToday()
                              ? "aero:text-primary"
                              : "aero:text-muted-foreground"
                          }`}
                        >
                          {dayjs(day).format("ddd")}
                        </p>
                        <p
                          className={`aero:text-xl aero:font-bold aero:leading-none ${
                            dayjs(day).isToday()
                              ? "aero:text-primary"
                              : "aero:text-foreground"
                          }`}
                        >
                          {dayjs(day).format("D")}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body: left panel fixed + right grid scrolls horizontally */}
        <div
          className={cn(
            "aero:flex",
            viewMode !== "daily" && "aero:border-t aero:border-neutral-300"
          )}
        >
          <div
            className="aero:flex-shrink-0 aero:border-r aero:border-neutral-300"
            style={{ width: `${leftPanelWidth}px` }}
          >
            {rowData.map((row) => (
              <div
                key={row.key}
                className="aero:border-b aero:border-neutral-300 aero:last:border-b-0"
                style={{ height: `${row.height}px` }}
              >
                {row.left}
              </div>
            ))}
            {isEmpty && rowData.length === 0 && (
              <div className="aero:flex aero:items-center aero:justify-center aero:py-16 aero:px-4">
                <div className="aero:flex aero:flex-col aero:items-center aero:gap-3 aero:text-center aero:max-w-md">
                  <div className="aero:rounded-full aero:bg-muted aero:p-4">
                    <Calendar className="aero:h-8 aero:w-8 aero:text-muted-foreground" />
                  </div>
                  <div className="aero:space-y-1">
                    <h3 className="aero:font-semibold aero:text-base aero:text-foreground">
                      No Schedule Data
                    </h3>
                    <p className="aero:text-sm aero:text-muted-foreground">
                      {emptyMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Right grid: scrollbar only appears here — not under the left panel */}
          <div
            ref={bodyScrollRef}
            className="aero:flex-1 aero:overflow-x-auto"
            onScroll={syncHeaderScroll}
          >
            <div
              style={{
                minWidth: `${gridMinWidth + 20}px`,
                paddingLeft: "20px"
              }}
            >
              {rowData.map((row) => (
                <div
                  key={row.key}
                  className="aero:border-b aero:border-neutral-300 aero:last:border-b-0"
                  style={{ height: `${row.height}px` }}
                >
                  {row.right}
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="aero:p-0">
      <div className="aero:overflow-x-auto">
        <div
          style={{
            minWidth: `${leftPanelWidth + gridMinWidth}px`
          }}
        >
          {viewMode === "daily" ? (
            /*
             * Daily view: left "Job Orders" panel spans BOTH the date row and time-slot row
             * so they appear merged into one unified header block (matches Figma 236-109908).
             */
            <div className="aero:flex aero:border-b aero:border-border/50">
              {/* Left panel — full height, merged */}
              <div
                className="aero:flex-shrink-0 aero:border-r aero:border-border/50 aero:bg-muted/20 aero:flex aero:flex-col aero:justify-center aero:pt-8 aero:pb-4 aero:pl-5 aero:pr-4"
                style={{ width: `${leftPanelWidth}px` }}
              >
                <div className="aero:flex aero:items-center aero:justify-between aero:mb-1">
                  <span className="aero:font-semibold aero:text-sm">
                    {rowLabelHeader}
                  </span>
                  {headerAction}
                </div>
                {rowLabelDescription && (
                  <p className="aero:text-[10px] aero:font-normal aero:text-muted-foreground aero:leading-tight">
                    {rowLabelDescription}
                  </p>
                )}
              </div>

              {/* Right side: date row stacked above time-slot row */}
              <div className="aero:flex-1 aero:flex aero:flex-col">
                {/* Date row */}
                <div className="aero:border-b aero:border-t aero:border-neutral-300 aero:bg-muted/20 aero:py-3 aero:px-5">
                  <p className="aero:text-xs aero:font-semibold aero:uppercase aero:tracking-widest aero:text-muted-foreground aero:leading-none aero:mb-1">
                    {dayjs(timelineData.startDate).format("ddd")}
                  </p>
                  <p
                    className={`aero:text-2xl aero:font-bold aero:leading-none ${dayjs(timelineData.startDate).isToday() ? "aero:text-primary" : "aero:text-foreground"}`}
                  >
                    {dayjs(timelineData.startDate).format("D")}
                  </p>
                </div>
                {/* Time-slot row */}
                <div className="aero:flex aero:bg-muted/30">
                  {timeSlots.map((slot) => (
                    <div
                      key={slot.hour}
                      className={cn(
                        "aero:flex-shrink-0 aero:p-2 aero:text-center aero:text-xs aero:font-medium aero:border-r aero:last:border-r-0",
                        slot.hour >= 8 && slot.hour < 17
                          ? "aero:text-green-700 aero:font-semibold"
                          : "aero:text-muted-foreground"
                      )}
                      style={{ width: colWidth }}
                    >
                      {slot.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Weekly/Monthly: single header row with day columns */
            <div className="aero:flex aero:border-b">
              <div
                className="aero:flex-shrink-0 aero:py-3 aero:pl-4 aero:pr-3 aero:border-r aero:font-semibold aero:text-sm"
                style={{ width: `${leftPanelWidth}px` }}
              >
                <div className="aero:flex aero:items-center aero:justify-between">
                  <span>{rowLabelHeader}</span>
                  {headerAction}
                </div>
                {rowLabelDescription && (
                  <p className="aero:text-[10px] aero:font-normal aero:text-muted-foreground aero:mt-0.5 aero:leading-tight">
                    {rowLabelDescription}
                  </p>
                )}
              </div>
              {timelineData.days.map((day) => {
                const isTodayDay = dayjs(day).isToday();
                const holidayName = getHolidayName(day, holidays);
                const isHoliday = !!holidayName;
                const isWeekend = isWeekendDay(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      "aero:flex-shrink-0 aero:p-2 aero:text-center aero:text-xs aero:font-medium aero:border-r aero:last:border-r-0",
                      // Precedence today > holiday > weekend (WOR-2187).
                      isTodayDay
                        ? "aero:bg-primary/10 aero:text-primary"
                        : isHoliday
                          ? "aero:bg-yellow-200"
                          : isWeekend
                            ? "aero:bg-gray-200"
                            : ""
                    )}
                    style={{ width: colWidth }}
                  >
                    <div>{dayjs(day).format("ddd")}</div>
                    <div
                      className={isTodayDay ? "aero:font-bold" : "aero:font-normal"}
                    >
                      {dayjs(day).format("MMM D")}
                    </div>
                    {isHoliday && (
                      <div
                        className="aero:text-[10px] aero:font-medium aero:leading-tight aero:text-amber-700 aero:truncate"
                        title={holidayName}
                      >
                        {holidayName}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Calendar Content - children will render rows */}
          {children}
        </div>
      </div>
    </CardContent>
  );
}
